import { Button, ButtonColor, ButtonFill, ButtonSize } from "components/Button";
import { getButtonRoleColor } from "util/contextColor";
import { ClipboardIcon, DownloadIcon } from "@radix-ui/react-icons";
import UserDataDialog from "components/Dashboard/UserDataDialog";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/Tooltip";
import {
  Auth0AccountRole,
  StudentLimitedResponse,
  ApiError,
  ParentLimitedResponse,
  TutorLimitedResponse,
} from "client/openapi";
import Notifications from "util/notifications";
import { ExportsService } from "client/openapi";
import { PersonKind } from "client/openapi/models/PersonKind";
import ArchivePersonButton from "pages/manage/ProfilePageResources/ArchivePersonButton";
import { saveAs } from "file-saver";
import { PageStatus } from "types";
import "../index.css";
import { useContext, useRef } from "react";
import { OrgRolesAndAccountContext } from "util/OrgRolesAccountContext";
import SectionTitle from "components/SectionTitle";
import { BubbleTone } from "components/IconBubble";

/** What differs by kind of person. */
const BY_TYPE = {
  [Auth0AccountRole.ME]: {
    exportCsv: ExportsService.exportStudents,
    label: "student",
    path: "students",
    kind: PersonKind.STUDENT,
  },
  [Auth0AccountRole.PARENT]: {
    exportCsv: ExportsService.exportParents,
    label: "parent",
    path: "parents",
    kind: PersonKind.PARENT,
  },
  [Auth0AccountRole.ORG_TUTOR]: {
    exportCsv: ExportsService.exportTutors,
    label: "tutor",
    path: "tutors",
    kind: PersonKind.TUTOR,
  },
} as const;

export default function AdministrativeInfo({
  exportingData,
  setExportingData,
  userType,
  student,
  parent,
  tutor,
  organization_id,
  exportingMeetings,
  showViewProfileButton,
  onArchived,
  setStatus,
  setError,
}: {
  exportingData: boolean;
  setExportingData: (boolean) => void;
  userType: Auth0AccountRole;
  student?: StudentLimitedResponse;
  parent?: ParentLimitedResponse;
  tutor?: Pick<
    TutorLimitedResponse,
    "id" | "org_id" | "first_name" | "last_name"
  >;
  organization_id: number;
  exportingMeetings?: boolean;
  showViewProfileButton?: boolean;
  /**
   * When given, Archive sits beside Export (a CRM board card); called once
   * archived. Both are then icons with tooltips, beside View Profile.
   */
  onArchived?: () => void;
  setStatus?: (PageStatus) => void;
  setError?: (APIResponse) => void;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);

  const { currently_selected_role } = useContext(OrgRolesAndAccountContext);
  const row = BY_TYPE[userType as keyof typeof BY_TYPE];
  // A board card's data window: Export and Archive as icons.
  const iconsOnly = Boolean(onArchived);
  const person =
    userType === Auth0AccountRole.PARENT
      ? parent
      : userType === Auth0AccountRole.ORG_TUTOR
      ? tutor
      : student;

  async function exportUser() {
    if (!person) {
      return Notifications.error("Unable to export data.");
    }
    if (exportingMeetings) {
      return Notifications.error("Please wait for other export to complete.");
    }
    setExportingData(true);
    await row
      .exportCsv({ requestBody: [person.id], orgId: organization_id })
      .then((data) => {
        try {
          const blob = new Blob([data], { type: "text/csv" });
          saveAs(
            blob,
            `${person.first_name}_${person.last_name}_data_export.csv`
          );
          Notifications.success(
            `${person.first_name}'s data export has been saved to your computer!`
          );
        } catch (error) {
          console.error(error);
          Notifications.error("Unable to save file.");
        }
      })
      .catch((e: ApiError) => {
        setStatus?.(PageStatus.ERROR);
        setError?.({ message: `Unable to export ${row.label}'s data` });
        console.error(`Error (#${e.status}): ${e.message}`);
      });
    setExportingData(false);
  }

  // If no role is selected, return null instead of undefined
  if (!currently_selected_role) {
    return null;
  }

  const exportButton = (
    <Button
      color={getButtonRoleColor(
        currently_selected_role ? currently_selected_role : Auth0AccountRole.ME
      )}
      size={ButtonSize.SMALL}
      onClick={() => exportUser()}
      aria-label={iconsOnly ? `Export ${person?.first_name}'s data` : undefined}
    >
      {!iconsOnly && "Export "}
      {exportingData ? (
        <span
          className="spinner-border spinner-border-square"
          role="status"
          aria-hidden="true"
        ></span>
      ) : iconsOnly ? (
        <DownloadIcon aria-hidden />
      ) : (
        <DownloadIcon aria-label="Export Data" />
      )}
    </Button>
  );

  return (
    <section
      className="student-profile--section data-card"
      id="student-about"
      ref={dialogRef}
    >
      {/* The name never splits; when narrow, the buttons take a second row. */}
      <div className="student-profile--section-header flex-wrap gap-x-3 gap-y-2">
        <SectionTitle icon={<ClipboardIcon />} tone={BubbleTone.INDIGO}>
          {person?.first_name}'s Data
        </SectionTitle>

        {/* The buttons sit directly in the row, so they share one height
            and center on one line. */}
        <div className="ml-auto flex shrink-0 items-stretch gap-2 whitespace-nowrap">
          {showViewProfileButton && (
            <Button
              color={ButtonColor.PURPLE}
              size={ButtonSize.SMALL}
              onClick={() =>
                window.open(
                  `${process.env.REACT_APP_FRONTEND_DOMAIN}/manage/${row.path}/${person?.id}`,
                  "_blank"
                )
              }
            >
              View Profile
            </Button>
          )}
          {iconsOnly ? (
            <Tooltip>
              <TooltipTrigger>{exportButton}</TooltipTrigger>
              <TooltipContent>Export</TooltipContent>
            </Tooltip>
          ) : (
            exportButton
          )}
          {onArchived && person && (
            <ArchivePersonButton
              kind={row.kind}
              person={person}
              size={ButtonSize.SMALL}
              withDelete={false}
              iconOnly
              onChanged={(archived_at) => archived_at && onArchived()}
            />
          )}
        </div>
      </div>

      <div className="student-profile--section-items">
        <div className="student-profile--section-item">
          <UserDataDialog
            beingViewedBy={currently_selected_role}
            userType={userType}
            student={student}
            parent={parent}
            tutor={tutor}
            dialogRef={dialogRef}
          />
        </div>
      </div>
    </section>
  );
}
